import { MapPin, Navigation, Phone } from "lucide-react";
import { Button, Section, SectionHeading } from "@/components/ui";
import { INDIA_OFFICE, indiaMapEmbedUrl, indiaMapLinkUrl } from "@/lib/indiaContact";

const MAP_TITLE = `Map of the A Learning Lab office, ${INDIA_OFFICE.city}`;

/** Embedded Google Map of the Gurgaon office (lazy-loaded iframe). */
export function ContactMapEmbed({ testId = "contact-map-frame", compact = false, className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-brand-500 bg-white shadow-soft ${className}`}
      data-testid={testId}
    >
      <iframe
        title={MAP_TITLE}
        src={indiaMapEmbedUrl()}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
        className={`block w-full border-0 ${compact ? "h-40 sm:h-44" : "h-72 sm:h-96 lg:h-[26rem]"}`}
      />
    </div>
  );
}

/**
 * "Find us in India": an embedded map of the Gurgaon office next to a small details card.
 * The map is lazy loaded so it never slows the first paint, and it has a text alternative
 * (the address card and the "Get directions" link) for people who skip iframes.
 */
export function ContactMap() {
  return (
    <Section tone="brandTint" id="map" labelledBy="contact-map-title" testId="contact-map" spacing="compact">
      <SectionHeading
        id="contact-map-title"
        className="max-w-none"
        titleClassName="sm:whitespace-nowrap"
        eyebrow="Let's connect"
        title="Come say hello. We'd love to meet you."
        intro="Drop in, call or message us on WhatsApp. We love a good “what should I do with my life?” chat."
      />

      <div className="mt-8 grid gap-4 sm:mt-10 sm:gap-6 lg:grid-cols-12">
        <ContactMapEmbed className="rounded-5xl lg:col-span-8" />

        <article
          className="flex flex-col gap-4 rounded-5xl border border-brand-200 bg-white p-5 sm:p-6 lg:col-span-4"
          data-testid="contact-map-card"
        >
          <h3 className="font-heading text-2xl font-bold leading-tight">Get in touch</h3>

          <ul className="space-y-3 text-sm text-ink/80 sm:text-base">
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" aria-hidden="true" />
              <address className="not-italic">{INDIA_OFFICE.address}</address>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" aria-hidden="true" />
              <a href={`tel:${INDIA_OFFICE.tel}`} className="font-semibold hover:text-brand-600">
                {INDIA_OFFICE.phone}
              </a>
            </li>
          </ul>

          <div className="mt-auto flex flex-col gap-3 pt-2">
            <Button href={indiaMapLinkUrl()} external size="md" className="w-full" data-testid="contact-map-directions">
              <Navigation className="h-4 w-4" aria-hidden="true" />
              Get directions
            </Button>
            <Button to="/consultation" variant="outline" size="md" className="w-full">
              Book a free chat
            </Button>
          </div>
        </article>
      </div>
    </Section>
  );
}
